import { theme, fontStack, radius } from "../theme";

/**
 * La conversation signalée, rendue COMME DANS L'APPLICATION (demande d'Henri,
 * v13.11) : des bulles, un auteur, et un horodatage SUR CHAQUE message.
 *
 * Pourquoi ça compte : le modérateur n'est pas informaticien. Un dump JSON du
 * coffre lui demande de reconstituer mentalement qui a écrit quoi, et quand —
 * exactement l'effort qu'on ne peut pas lui demander au moment où il décide d'un
 * bannissement. Ici il RELIT la scène.
 *
 * Repères visuels : la personne signalée est à gauche, marquée « signalé » ; le
 * déclarant est à droite (c'est lui qui nous parle). Un message effacé depuis la
 * capture le dit — le coffre garde la preuve, l'écran ne fait pas semblant que
 * rien n'a bougé.
 */

export interface TranscriptEntry {
  id?: string;
  authorUserId?: string | null;
  authorName?: string | null;
  text?: string | null;
  startedAt?: string;
  endedAt?: string;
  wasDeleted?: boolean;
  position?: number;
}

export interface TranscriptMedia {
  id?: string;
  attachmentId?: string;
  mimeType?: string | null;
  fileName?: string | null;
  sizeBytes?: number | null;
  blobAvailable?: boolean;
}

function timeOf(iso?: string): string {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ""
    : d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
}

function dayOf(iso?: string): string {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ""
    : d.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
}

export function formatBytes(n?: number | null): string {
  if (!n || n <= 0) return "";
  if (n < 1024) return `${n} o`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} Ko`;
  return `${(n / (1024 * 1024)).toFixed(1)} Mo`;
}

export function Transcript({
  entries,
  media,
  reportedUserId,
  reporterId,
}: {
  entries: TranscriptEntry[];
  media?: TranscriptMedia[];
  reportedUserId?: string | null;
  reporterId?: string | null;
}) {
  if (entries.length === 0) {
    return (
      <div style={{ color: theme.ink3, fontSize: 14 }}>
        Aucun message n'a été capturé pour ce dossier.
      </div>
    );
  }

  let lastDay = "";

  return (
    <div>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {entries.map((e, i) => {
          const isReported = !!reportedUserId && e.authorUserId === reportedUserId;
          const isReporter = !!reporterId && e.authorUserId === reporterId;
          const day = dayOf(e.startedAt);
          const showDay = day && day !== lastDay;
          if (showDay) lastDay = day;

          return (
            <div key={e.id ?? i}>
              {showDay && (
                <div
                  style={{
                    textAlign: "center",
                    color: theme.ink3,
                    fontSize: 12,
                    margin: "14px 0 12px",
                  }}
                >
                  {day}
                </div>
              )}

              <div
                style={{
                  display: "flex",
                  justifyContent: isReporter ? "flex-end" : "flex-start",
                }}
              >
                <div style={{ maxWidth: "78%" }}>
                  {/* Auteur — nommé à chaque message : le modérateur ne doit
                      jamais avoir à remonter la conversation pour savoir qui parle. */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      marginBottom: 3,
                      justifyContent: isReporter ? "flex-end" : "flex-start",
                    }}
                  >
                    <span
                      style={{
                        fontFamily: fontStack.uiSemiBold,
                        fontSize: 12.5,
                        color: isReported ? theme.error : theme.ink2,
                      }}
                    >
                      {e.authorName || "Auteur inconnu"}
                    </span>
                    {isReported && (
                      <span
                        style={{
                          fontSize: 10.5,
                          color: theme.error,
                          border: `1px solid ${theme.error}`,
                          borderRadius: radius.pill,
                          padding: "0 7px",
                          lineHeight: "16px",
                        }}
                      >
                        signalé
                      </span>
                    )}
                  </div>

                  <div
                    style={{
                      background: isReporter ? theme.surface3 : theme.surface2,
                      border: `1px solid ${isReported ? theme.error : theme.line}`,
                      borderRadius: radius.bubble,
                      padding: "10px 14px",
                      color: theme.ink,
                      fontSize: 14.5,
                      lineHeight: 1.45,
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                    }}
                  >
                    {e.text ? (
                      e.text
                    ) : (
                      <span style={{ color: theme.ink3, fontStyle: "italic" }}>
                        (message sans texte — voir les pièces jointes)
                      </span>
                    )}
                  </div>

                  {/* Horodatage SOUS chaque message (demande explicite). */}
                  <div
                    style={{
                      marginTop: 3,
                      fontSize: 11.5,
                      color: theme.ink3,
                      textAlign: isReporter ? "right" : "left",
                    }}
                  >
                    {timeOf(e.startedAt)}
                    {e.wasDeleted && (
                      <span style={{ color: theme.warning, marginLeft: 8 }}>
                        · supprimé par son auteur depuis la capture
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {media && media.length > 0 && (
        <div style={{ marginTop: 18, borderTop: `1px solid ${theme.line}`, paddingTop: 14 }}>
          <div style={{ fontFamily: fontStack.uiSemiBold, fontSize: 13, marginBottom: 8 }}>
            Pièces jointes de la conversation ({media.length})
          </div>
          <ul style={{ listStyle: "none", display: "flex", flexDirection: "column", gap: 6 }}>
            {media.map((m, i) => (
              <li
                key={m.id ?? i}
                style={{ fontSize: 13, color: theme.ink2, display: "flex", gap: 8, flexWrap: "wrap" }}
              >
                <span>{m.fileName || m.mimeType || "pièce jointe"}</span>
                {m.sizeBytes ? <span style={{ color: theme.ink3 }}>{formatBytes(m.sizeBytes)}</span> : null}
                {/* Honnêteté du libellé : le gel ne survit pas à la clôture du
                    compte d'un participant — on dit si l'octet est encore là. */}
                <span style={{ color: m.blobAvailable ? theme.ink3 : theme.warning }}>
                  {m.blobAvailable ? "· fichier conservé" : "· fichier supprimé (non consultable)"}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
